/**
 * The supplier portal's quote request pages (docs/features/supplier-interface/33): their wording, how money is
 * read and shown, and the answer a price form sends.
 *
 * All wording here is provisional (spec 33, B5) and follows `docs/ui-wording.md`. The server words its own status
 * labels and refusals; this file words only what the pages say themselves.
 */

import { format, parseISO } from "date-fns";
import type {
  QuotePriceRowInput,
  QuoteRequestAnswer,
} from "../data/supplier-portal-adapter";
import type {
  QuoteRequestClosedReason,
  QuoteRequestStatus,
} from "../data/supplier-api-types";
import { newUuid } from "./object-ui/command-run";
import type { SupplierTone } from "./supplier-portal-wording";

export const QUOTE_REQUEST_WORDING = {
  title: (supplierQuoteNo: string) =>
    `Quote request ${supplierQuoteNo} from Pollards`,
  linkScope: (supplierQuoteNo: string) =>
    `You're viewing quote request ${supplierQuoteNo} only. Sign in to see all your orders and invoices.`,
  replyBy: "Reply by",
  whatWeNeed: "What we need",
  specification: "Specification",
  deliveredTo: "Deliver to",
  wantedBy: "Wanted by",
  files: "Files",
  yourPrices: "Your prices, delivered",
  pricesHint:
    "Give the total price for each quantity, delivered to the address above, excluding VAT.",
  quantity: "Quantity",
  yourPrice: "Your price (£)",
  cantQuote: "Can't quote",
  despatchBy: "Despatch by",
  validUntil: "Valid until (required)",
  yourReference: "Your reference (required)",
  yourQuoteOptional: "Your quote (PDF) (optional)",
  yourQuote: "Your quote (PDF)",
  attach: "Attach…",
  sendPrices: "Send our prices",
  changePrices: "Change our prices",
  cancelChange: "Keep the prices we sent",
  cantQuoteForThis: "We can't quote for this…",
  declineTitle: "We can't quote for this",
  declineWhy: "Why can't you quote?",
  declineSend: "Tell Pollards",
  sent: "Thanks. Pollards has your prices. You can change them here until Pollards sends its quote to its customer.",
  declinedLine: "You told Pollards you can't quote for this.",
  list: {
    heading: "Quote requests",
    empty: "No quote requests yet.",
    columns: {
      request: "Quote request",
      what: "What",
      quantities: "Quantities",
      replyBy: "Reply by",
      status: "Status",
    },
  },
  linkNotValidTitle: "This link no longer works",
  linkNotValidWhy: "It may have expired, or the request may be closed.",
  linkNotValidNext:
    "Sign in to see your quote requests, or ask your Pollards contact for a new link.",
  notFoundTitle: "We can't find this quote request",
  notFoundWhy:
    "It isn't one of your quote requests, or the number isn't right. Check the number on the email.",
  errorFallback: "We couldn't send this to Pollards.",
  downloadFailed:
    "We couldn't download this file. Please try again, or ask your Pollards contact to email it.",
  pdfOnly: "Upload a PDF",
  pdfTooLarge: "The file can't be over 25 MB.",
} as const;

/** Why a request is closed, in the line the page shows under its header. */
export const CLOSED_LINES: Record<QuoteRequestClosedReason, string> = {
  sent_to_customer:
    "Pollards has sent its quote to its customer, so these prices can't be changed now.",
  asked_again:
    "Pollards has changed what it needs and sent you a new request. This one is closed.",
  no_longer_needed:
    "Pollards no longer needs prices for this request. Thank you.",
  declined: QUOTE_REQUEST_WORDING.declinedLine,
};

export function statusTone(status: QuoteRequestStatus): SupplierTone {
  switch (status) {
    case "needs_prices":
      return "todo";
    case "prices_sent":
      return "ok";
    case "overdue":
      return "bad";
    case "closed":
      return "mute";
  }
}

/** "Thu 22 Oct". */
export function weekdayDayMonth(isoDate: string): string {
  return format(parseISO(isoDate), "EEE d MMM");
}

/** "500", "2,500". */
export function quantityLabel(quantity: number): string {
  return quantity.toLocaleString("en-GB");
}

/** "209 KB", "1.4 MB". */
export function fileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Money: pounds on screen, pence on the wire ──

const POUNDS = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});

/** "£3,120.00" from pence. */
export function poundsFromPence(pence: number): string {
  return POUNDS.format(pence / 100);
}

/** "3,120.00": the price form's input, prefilled from pence. */
export function inputFromPence(pence: number): string {
  return (pence / 100).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Pence from what a person typed in pounds ("3,120.00", "£3120", "12.5"). Null when it isn't an amount of pounds and
 * pence, so the server's field error ("Enter your price for 500, or tick Can't quote.") says what to fix.
 */
export function penceFromInput(text: string): number | null {
  const cleaned = text.replace(/[£,\s]/g, "");
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) return null;
  const pence =
    Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
  return Number.isSafeInteger(pence) ? pence : null;
}

// ── The answer a price form sends ──

export interface QuotePriceFormRow {
  quantity: number;
  /** What the person typed, in pounds. */
  price: string;
  is_cant_quote: boolean;
}

export interface QuoteAnswerForm {
  rows: readonly QuotePriceFormRow[];
  despatch_by: string;
  valid_until: string;
  your_reference: string;
}

/**
 * The write's `answer` from the form: a ticked Can't quote sends no price (the price typed is ignored, as the server
 * ignores it), and empty optional fields send nothing. `requestId` is the submission's, made once by the form.
 */
export function buildQuoteAnswer(
  form: QuoteAnswerForm,
  requestId: string,
): QuoteRequestAnswer {
  const prices: QuotePriceRowInput[] = form.rows.map((row) => ({
    quantity: row.quantity,
    cost: row.is_cant_quote ? null : penceFromInput(row.price),
    not_quoted: row.is_cant_quote,
  }));
  return {
    prices,
    their_reference: form.your_reference.trim(),
    valid_until: form.valid_until,
    despatch_by: form.despatch_by || null,
    request_id: requestId,
  };
}

/**
 * The `request_id` of the submission a form is making: the same one however many times it is retried after a failure
 * (the server then changes nothing twice), a new one once the submission went through. Use `current()` to submit and
 * `renew()` after success.
 */
export function submissionRequestIds(makeId: () => string = newUuid): {
  current: () => string;
  renew: () => void;
} {
  let id: string | null = null;
  return {
    current: () => (id ??= makeId()),
    renew: () => {
      id = null;
    },
  };
}
